import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { get, photoUrl, qs } from '../lib/api.ts';
import type { Me } from '../lib/session.ts';
import { Icon, type IconName } from './Icon.tsx';
import { EASE } from './motion.tsx';
import { Avatar } from './ui.tsx';

type Group = { label: string; items: { to: string; label: string; icon: IconName }[] };

export function CommandPalette({ open, onClose, groups, me }: { open: boolean; onClose: () => void; groups: Group[]; me: Me }) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 160);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    if (open) {
      setQ('');
      setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  const people = useQuery({
    queryKey: ['palette-people', debounced],
    queryFn: () => get<{ items: { id: string; name: string; jobPosition: string | null; employeeNo: string | null; photoFileId: string | null }[] }>(`/api/people${qs({ q: debounced, size: 6 })}`),
    enabled: open && me.access.people && debounced.length >= 2,
  });

  const results = useMemo(() => {
    const needle = q.toLowerCase();
    const pages = groups.flatMap((g) => g.items.map((i) => ({ kind: 'page' as const, key: i.to, label: i.label, hint: g.label, icon: i.icon, go: () => navigate({ to: i.to }) }))).filter((p) => !needle || p.label.toLowerCase().includes(needle) || p.hint.toLowerCase().includes(needle));
    const ppl = (people.data?.items ?? []).map((p) => ({ kind: 'person' as const, key: p.id, label: p.name, photo: p.photoFileId ? photoUrl(p.id, { version: p.photoFileId }) : null, hint: [p.jobPosition, p.employeeNo].filter(Boolean).join(' · '), icon: 'user' as IconName, go: () => navigate({ to: '/people/$id', params: { id: p.id } }) }));
    return [...ppl, ...pages].slice(0, 12);
  }, [q, groups, people.data, navigate]);

  useEffect(() => setCursor(0), [debounced]);

  const choose = (i: number) => {
    const r = results[i];
    if (!r) return;
    r.go();
    onClose();
  };

  return createPortal(
    <AnimatePresence>
      {open ? (
        <>
          <motion.div className="scrim" onClick={onClose} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Search"
            className="dialog"
            style={{ top: '18%', width: 'min(620px, calc(100vw - 32px))' }}
            initial={{ opacity: 0, x: '-50%', y: 12, scale: 0.98 }}
            animate={{ opacity: 1, x: '-50%', y: 0, scale: 1 }}
            exit={{ opacity: 0, x: '-50%', y: 6 }}
            transition={{ duration: 0.32, ease: EASE }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setCursor((c) => Math.min(c + 1, results.length - 1));
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setCursor((c) => Math.max(c - 1, 0));
              }
              if (e.key === 'Enter') {
                e.preventDefault();
                choose(cursor);
              }
            }}
          >
            <div className="input-group" style={{ padding: 14, borderBottom: '1px solid var(--line-1)' }}>
              <Icon name="search" style={{ left: 28 }} />
              <input
                ref={inputRef}
                className="input"
                style={{ border: 0, background: 'transparent', boxShadow: 'none', height: 44, fontSize: 'var(--step-1)' }}
                placeholder={me.access.people ? 'Search people or jump to a page…' : 'Jump to a page…'}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                role="combobox"
                aria-expanded="true"
                aria-controls="palette-list"
                aria-activedescendant={results[cursor] ? `pal-${results[cursor]!.key}` : undefined}
              />
              {people.isFetching ? <span className="spinner" style={{ position: 'absolute', right: 28 }} /> : null}
            </div>
            <ul id="palette-list" role="listbox" style={{ listStyle: 'none', margin: 0, padding: 8, maxHeight: 380, overflow: 'auto' }}>
              {results.length === 0 ? <li className="faint" style={{ padding: 18, textAlign: 'center' }}>Nothing matches “{q}”.</li> : null}
              {results.map((r, i) => (
                <li
                  key={`${r.kind}-${r.key}`}
                  id={`pal-${r.key}`}
                  role="option"
                  aria-selected={i === cursor}
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => choose(i)}
                  className="row"
                  style={{ padding: '10px 12px', borderRadius: 8, cursor: 'pointer', background: i === cursor ? 'var(--bg-3)' : 'transparent', ['--gap' as string]: '12px' }}
                >
                  {r.kind === 'person' ? <Avatar name={r.label} size={28} photo={'photo' in r ? r.photo : null} /> : <span style={{ width: 28, display: 'grid', placeItems: 'center', color: 'var(--ink-3)' }}><Icon name={r.icon} width={17} height={17} /></span>}
                  <span>{r.label}</span>
                  <span className="faint" style={{ fontSize: 'var(--step--2)' }}>
                    {r.hint}
                  </span>
                  {i === cursor ? <Icon name="arrowRight" width={15} height={15} style={{ marginLeft: 'auto', color: 'var(--accent-text)' }} /> : null}
                </li>
              ))}
            </ul>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
